import { useStore } from "zustand";
import { useUserProfile } from "@app/hooks/useIMS";
import { store } from "../store";

const EMPTY_ROLES = [];

/**
 * React hook returning the signed-in user's roles.
 *
 * @param {Object} [options]
 * @param {boolean} [options.ignoreOverride=false] Return the assigned roles even
 *   when roles are disabled via the Admin menu.
 * @returns {string[]} User roles
 */
export function useUserRoles({ ignoreOverride = false } = {}) {
  const user = useUserProfile();
  const rolesDisabled = useStore(store, (state) => state.rolesDisabled);

  if (rolesDisabled && !ignoreOverride) {
    return EMPTY_ROLES;
  }
  return Array.isArray(user?.roles) ? user.roles : EMPTY_ROLES;
}

/**
 * React hook checking whether the signed-in user has any of the given roles.
 * Admins implicitly pass every check.
 *
 * @param {string|string[]} roles Role or list of roles (any match passes)
 * @param {Object} [options]
 * @param {boolean} [options.ignoreOverride=false] Check the assigned roles even
 *   when roles are disabled via the Admin menu.
 * @returns {boolean}
 */
export function useHasRole(roles, options) {
  const userRoles = useUserRoles(options);
  return (
    userRoles.includes("admin") ||
    [].concat(roles).some((role) => userRoles.includes(role))
  );
}

/**
 * React hook checking whether the signed-in user is an admin.
 *
 * @param {Object} [options]
 * @param {boolean} [options.ignoreOverride=false]
 * @returns {boolean}
 */
export function useIsAdmin(options) {
  return useHasRole("admin", options);
}

/**
 * React hook checking whether dev mode is enabled. Defaults to
 * `process.env.NODE_ENV === "development"`; admins can override it from the
 * Admin menu.
 *
 * @returns {boolean}
 */
export function useIsDevMode() {
  const isAdmin = useIsAdmin({ ignoreOverride: true });
  const devModeOverride = useStore(store, (state) => state.devModeOverride);

  if (isAdmin && typeof devModeOverride === "boolean") {
    return devModeOverride;
  }
  return process.env.NODE_ENV === "development";
}
